import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AlertService } from '@services/utils/alert.service';
import { UsersService } from '@services/api/internal/users.service';
import {
  getCurrentUser,
  getCurrentUserFailure,
  getCurrentUserSuccess,
  setOrderHistoryViewType,
  setUserLanguage,
  setUserLanguageFailure,
  setUserLanguageLocal,
  setUserLanguageSuccess,
  updateBalanceInfo,
  updateBalanceInfoFailure,
  updateBalanceInfoPending,
  updateBalanceInfoSuccess,
} from './user.actions';
import { catchError, defaultIfEmpty, map, of, startWith, switchMap, tap, withLatestFrom } from 'rxjs';
import { AlertType } from '@shared/enums/notifications/notifications.enum';
import { HttpErrorResponse } from '@angular/common/http';
import { ContextFacade } from '../../core';
import { TranslocoService } from '@jsverse/transloco';
import { selectUserLanguage } from './user.selectors';
import { Store } from '@ngrx/store';
import { TranslocoLocaleService } from '@jsverse/transloco-locale';
import { resolveIntlLocale } from '@shared/utils/intl-locale.util';

@Injectable()
export class UserEffects {
  constructor(
    private actions$: Actions,
    private userService: UsersService,
    private contextFacade: ContextFacade,
    private alertService: AlertService,
    private translateService: TranslocoService,
    private localeService: TranslocoLocaleService,
    private store: Store,
  ) {}

  getCurrentUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getCurrentUser),
      switchMap(() =>
        this.userService.getMe().pipe(
          map(user => getCurrentUserSuccess({ user })),
          defaultIfEmpty(getCurrentUserFailure()),
          catchError((error: HttpErrorResponse) => {
            this.alertService.show({ severity: AlertType.Error, summary: error.message });
            return of(getCurrentUserFailure());
          }),
        ),
      ),
    ),
  );

  updateBalanceInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateBalanceInfo),
      switchMap(() => this.contextFacade.contract),
      switchMap(contract => {
        if (!contract?.id) {
          return of(updateBalanceInfoPending({ contractId: null }), updateBalanceInfoFailure());
        }

        return this.userService.getBalanceInfoFull(contract.id).pipe(
          map(balanceInfo => updateBalanceInfoSuccess({ balanceInfo })),
          catchError(() => {
            this.alertService.show({
              severity: AlertType.Error,
              summary: 'USER_PROFILE.ERROR_UPDATING_BALANCE',
            });
            return of(updateBalanceInfoFailure());
          }),
          startWith(updateBalanceInfoPending({ contractId: contract.id })),
        );
      }),
    ),
  );

  setUserLanguageLocal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(setUserLanguageLocal),
        withLatestFrom(this.store.select(selectUserLanguage)),
        tap(([{ language }]) => {
          const languageCode = language.description.split('-')[0];
          this.localeService.setLocale(resolveIntlLocale(language.description));
          this.translateService.setActiveLang(languageCode);
        }),
      ),
    { dispatch: false },
  );

  setUserLanguage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setUserLanguage),
      switchMap(({ language }) =>
        this.userService.setUserLanguage(language.id).pipe(
          map(() => {
            const languageCode = language.description.split('-')[0];
            this.translateService.setActiveLang(languageCode);
            this.localeService.setLocale(resolveIntlLocale(language.description));

            return setUserLanguageSuccess({
              language,
            });
          }),
          catchError(() => of(setUserLanguageFailure())),
        ),
      ),
    ),
  );

  setOrderHistoryViewType$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(setOrderHistoryViewType),
        switchMap(({ orderHistoryViewType }) => this.userService.setOrderHistoryTypeView(orderHistoryViewType.id)),
      ),
    { dispatch: false },
  );
}
